import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const API = '/api';
const FONT = "'Inter', system-ui, sans-serif";

const BluIcon = ({ d, size = 18, fill = "none", strokeWidth = 2, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle' }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ix = {
    building: ['M4 22h16', 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18', 'M10 10h4', 'M10 14h4'],
    card: ['M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M1 10h22'],
    wallet: ['M20 12V8H6a2 2 0 0 1 2-2h12V5a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2h-3', 'M20 12h-4a1 1 0 0 0 0 2h4v-2z'],
    ticket: ['M15 5v2', 'M15 11v2', 'M15 17v2', 'M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z']
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
    }).format(amount);
};

// Colors for the charts
const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function Dashboard({ user }) {
    const [operators, setOperators] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [opsRes, subsRes, tktRes, expRes] = await Promise.all([
                    axios.get(`${API}/operators`),
                    axios.get(`${API}/subscriptions`),
                    axios.get(`${API}/tickets`),
                    axios.get('/api/admin-expenses')
                ]);
                setOperators(opsRes.data || []);
                setSubscriptions(subsRes.data || []);
                setTickets(tktRes.data || []);
                setExpenses(expRes.data?.expenses || []);
            } catch (err) {
                console.error("Dashboard fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    // Derived Metrics
    const totalOperators = operators.length;
    const activeOperators = operators.filter(o => o.status === 'Active').length;
    const activeSubs = subscriptions.filter(s => s.status === 'Active').length;
    const totalRevenue = subscriptions.reduce((acc, curr) => acc + (curr.amount || curr.planId?.price || 0), 0);
    const totalExpenses = expenses.reduce((acc, curr) => acc + (curr.amount || 0), 0);
    const netProfit = totalRevenue - totalExpenses;
    const openTickets = tickets.filter(t => t.tags?.includes('To Superadmin') && t.status?.name !== 'Closed' && t.status?.name !== 'Resolved').length;

    // Process data for charts
    // 1. Revenue vs Expenses over the last 6 months
    const processChartData = () => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentMonth = new Date().getMonth();
        const dataMap = {};
        
        // Initialize last 6 months
        for (let i = 5; i >= 0; i--) {
            let m = currentMonth - i;
            if (m < 0) m += 12;
            dataMap[months[m]] = { name: months[m], Revenue: 0, Expenses: 0 };
        }

        // Add Revenue
        subscriptions.forEach(sub => {
            if (!sub.createdAt) return;
            const m = months[new Date(sub.createdAt).getMonth()];
            if (dataMap[m]) {
                dataMap[m].Revenue += (sub.amount || sub.planId?.price || 0);
            }
        });

        // Add Expenses
        expenses.forEach(exp => {
            if (!exp.date) return;
            const m = months[new Date(exp.date).getMonth()];
            if (dataMap[m]) {
                dataMap[m].Expenses += (exp.amount || 0);
            }
        });

        return Object.values(dataMap);
    };

    // 2. Ticket Status Distribution
    const processTicketData = () => {
        const sysTkt = tickets.filter(t => t.tags?.includes('To Superadmin'));
        const counts = sysTkt.reduce((acc, curr) => {
            const stat = curr.status?.name || 'Open';
            acc[stat] = (acc[stat] || 0) + 1;
            return acc;
        }, {});
        return Object.keys(counts).map(key => ({ name: key, value: counts[key] }));
    };

    const chartData = processChartData();
    const ticketData = processTicketData();

    const recentOperators = [...operators].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);
    const recentExpenses = [...expenses].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

    const Skeleton = () => (
        <div style={{ background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 4, height: '100%', width: '100%' }} />
    );

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '32px 40px', fontFamily: FONT, boxSizing: 'border-box', overflowY: 'auto' }}>
            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes fadeIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
                
                .stat-card { background: white; padding: 20px; border-radius: 12px; display: flex; align-items: center; gap: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); border: 1px solid #e2e8f0; border-top: 4px solid #0b1f50; animation: fadeIn 0.4s ease-out both; overflow: hidden; }
                .stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
                .stat-info { display: flex; flex-direction: column; }
                .stat-info .stat-val { font-size: 22px; font-weight: 700; color: #1e293b; }
                .stat-info .stat-lbl { font-size: 13px; color: #64748b; font-weight: 500; margin-bottom: 2px; }
                .stat-info .stat-sub { font-size: 11px; color: #94a3b8; font-weight: 500; }
                
                .section-card { background: white; border-radius: 8px; border: 1px solid #e2e8f0; padding: 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); animation: fadeIn 0.4s ease-out both; }
                .section-header { font-size: 16px; font-weight: 600; color: #0f172a; margin: 0 0 20px 0; border-bottom: 1px solid #f1f5f9; padding-bottom: 16px; }
                
                .data-table { width: 100%; border-collapse: collapse; text-align: left; }
                .data-table th { padding: 12px 16px; font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
                .data-table td { padding: 16px; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #334155; vertical-align: middle; }
                .data-table tr:hover td { background: #fcfcfd; }
                .data-table tr:last-child td { border-bottom: none; }
                
                .status-badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
                .status-active { background: #dcfce7; color: #166534; }
                .status-inactive { background: #f1f5f9; color: #475569; }
            `}</style>

            <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Platform Overview</h1>
                    <p style={{ margin: '6px 0 0 0', fontSize: 14, color: '#64748b' }}>System administration and financial analytics.</p>
                </div>
            </div>

            {/* KPI ROW */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24 }}>
                <div className="stat-card" style={{ animationDelay: '0.1s' }}>
                    <div className="stat-icon" style={{ background: '#f0f4ff', color: '#3b82f6' }}>
                        <BluIcon d={ix.building} size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-val">{loading ? <div style={{ height: 28, width: 60 }}><Skeleton /></div> : totalOperators}</span>
                        <span className="stat-lbl">Network Operators</span>
                        <span className="stat-sub">{activeOperators} Active Operators</span>
                    </div>
                </div>

                <div className="stat-card" style={{ animationDelay: '0.2s' }}>
                    <div className="stat-icon" style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
                        <BluIcon d={ix.card} size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-val">{loading ? <div style={{ height: 28, width: 60 }}><Skeleton /></div> : activeSubs}</span>
                        <span className="stat-lbl">Subscriptions</span>
                        <span className="stat-sub">Paying Platform Tenants</span>
                    </div>
                </div>

                <div className="stat-card" style={{ animationDelay: '0.3s' }}>
                    <div className="stat-icon" style={{ background: '#ecfdf5', color: '#10b981' }}>
                        <BluIcon d={ix.wallet} size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-val">{loading ? <div style={{ height: 28, width: 100 }}><Skeleton /></div> : formatCurrency(netProfit)}</span>
                        <span className="stat-lbl">Net Profit</span>
                        <span className="stat-sub">Total Revenue: {formatCurrency(totalRevenue)}</span>
                    </div>
                </div>

                <div className="stat-card" style={{ animationDelay: '0.4s' }}>
                    <div className="stat-icon" style={{ background: '#fffbeb', color: '#f59e0b' }}>
                        <BluIcon d={ix.ticket} size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-val">{loading ? <div style={{ height: 28, width: 60 }}><Skeleton /></div> : openTickets}</span>
                        <span className="stat-lbl">Open Tickets</span>
                        <span className="stat-sub">Pending Superadmin Action</span>
                    </div>
                </div>
            </div>

            {/* CHARTS ROW */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
                <div className="section-card" style={{ animationDelay: '0.5s', height: 400 }}>
                    <h3 className="section-header">Financial Performance (Revenue vs Expenses)</h3>
                    {loading ? (
                        <div style={{ height: 300, padding: 20 }}><Skeleton /></div>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorExp" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(value) => `$${value}`} />
                                <Tooltip 
                                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}
                                    formatter={(value) => formatCurrency(value)}
                                />
                                <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: 13 }} />
                                <Area type="monotone" dataKey="Revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" />
                                <Area type="monotone" dataKey="Expenses" stroke="#ef4444" strokeWidth={3} fillOpacity={1} fill="url(#colorExp)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className="section-card" style={{ animationDelay: '0.6s', height: 400 }}>
                    <h3 className="section-header">Support Tickets</h3>
                    {loading ? (
                        <div style={{ height: 300, padding: 20 }}><Skeleton /></div>
                    ) : ticketData.length === 0 ? (
                        <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14 }}>
                            No support tickets found
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={ticketData}
                                    cx="50%"
                                    cy="45%"
                                    innerRadius={70}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {ticketData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip 
                                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }} 
                                    itemStyle={{ color: '#0f172a', fontWeight: 600 }}
                                />
                                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 13 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* TABLES ROW */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div className="section-card" style={{ animationDelay: '0.7s', padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '20px 24px' }}>
                        <h3 className="section-header" style={{ margin: 0, border: 'none', padding: 0 }}>Recent Tenants</h3>
                    </div>
                    {loading ? (
                        <div style={{ padding: 24, height: 200 }}><Skeleton /></div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Company</th>
                                        <th>Country</th>
                                        <th>Registered</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentOperators.length === 0 ? (
                                        <tr><td colSpan="4" style={{ textAlign: 'center', color: '#94a3b8' }}>No operators found.</td></tr>
                                    ) : (
                                        recentOperators.map(op => (
                                            <tr key={op._id}>
                                                <td style={{ fontWeight: 600 }}>{op.companyName || 'N/A'}</td>
                                                <td>{op.country || 'N/A'}</td>
                                                <td>{new Date(op.createdAt).toLocaleDateString()}</td>
                                                <td>
                                                    <span className={`status-badge ${op.status === 'Active' ? 'status-active' : 'status-inactive'}`}>
                                                        {op.status || 'Pending'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="section-card" style={{ animationDelay: '0.8s', padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '20px 24px' }}>
                        <h3 className="section-header" style={{ margin: 0, border: 'none', padding: 0 }}>Recent System Expenses</h3>
                    </div>
                    {loading ? (
                        <div style={{ padding: 24, height: 200 }}><Skeleton /></div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Category</th>
                                        <th>Payee</th>
                                        <th style={{ textAlign: 'right' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentExpenses.length === 0 ? (
                                        <tr><td colSpan="4" style={{ textAlign: 'center', color: '#94a3b8' }}>No expenses recorded.</td></tr>
                                    ) : (
                                        recentExpenses.map(exp => (
                                            <tr key={exp._id}>
                                                <td>{new Date(exp.date).toLocaleDateString()}</td>
                                                <td style={{ fontWeight: 600 }}>{exp.category}</td>
                                                <td>{exp.payee}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 600, color: '#ef4444' }}>
                                                    {formatCurrency(exp.amount)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
